import { BoltIcon, BoltSlashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { getInitialLoanFields, IVisibleLogic } from 'config'
import { uwSummaryDefaultInputs } from 'pages/Loan/Underwriting/UnderwritingSummary/constants'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, InputVisibleLogic } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { inputGroups, IUwSummaryConfig, UwSummarySection, UwSummarySectionText } from './types'

export const UnderwritingSummaryConfig = () => {
  const loanInputs = getInitialLoanFields()
  const uwSummaryInputs = uwSummaryDefaultInputs()

  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState<boolean>()
  const [data, setData] = useState<IUwSummaryConfig>({
    [UwSummarySection.UnderwritingInformation]: {},
    [UwSummarySection.PropertyConstruction]: {},
    [UwSummarySection.BorrowerCreditInfo]: {},
    [UwSummarySection.BorrowerInterestSource]: {},
    [UwSummarySection.SeniorLienInformation]: {},
    [UwSummarySection.Exception]: {},
  })

  useEffect(() => {
    setLoading(true)
    getAdminConfig('uwConfig')
      .then((res) => {
        if (!res) return
        Object.keys(UwSummarySection).map((key) => {
          if (!res[key]) res[key] = {}
        })
        setData(res)
      })
      .finally(() => setLoading(false))
  }, [])

  const onVisible = (section: UwSummarySection, key: string, value: any, props: 'visible' | 'required') => {
    const newData = cloneDeep(data)
    if (!newData[section][key]) newData[section][key] = {}
    newData[section][key][props] = value
    setData(newData)
    setChanged(true)
  }

  const onVisibleLogic = (section: UwSummarySection, key: string, logic: IVisibleLogic[][]) => {
    const newData = cloneDeep(data)
    if (!newData[section][key]) newData[section][key] = {}
    newData[section][key].visibleLogic = logic
    setData(newData)
    setChanged(true)
  }

  const renderField = (section: UwSummarySection, key: string, index: number, title: string) => {
    const { visible = true, required = false, visibleLogic = [] } = data[section][key] || {}
    const Icon = visible ? EyeIcon : EyeSlashIcon
    const RequiredIcon = required ? BoltIcon : BoltSlashIcon

    return (
      <div className="col-span-full rounded-md border border-slate-200 p-3" key={section + key}>
        <div className="mb-2 flex items-center justify-between">
          <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>
            {index}. {title}
          </p>
          <div className="flex items-center gap-2">
            <span
              className={`btn-icon ${visible ? '' : 'opacity-60'}`}
              onClick={() => onVisible(section, key, !visible, 'visible')}
            >
              <Icon className="w-4 h-4" />
            </span>
            <span
              className={`btn-icon ${required ? '' : 'opacity-60'}`}
              onClick={() => onVisible(section, key, !required, 'required')}
            >
              <RequiredIcon className="w-4 h-4" />
            </span>
          </div>
        </div>
        <InputVisibleLogic
          id={`${section}-${key}-logic`}
          value={visibleLogic}
          onChange={(logic: IVisibleLogic[][]) => onVisibleLogic(section, key, logic)}
        />
      </div>
    )
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig('uwConfig', data)
    setLoading(false)
    setChanged(false)
  }

  return (
    <div>
      <LayoutLoading show={isLoading} />

      <div className="mb-4 mt-4 grid grid-cols-1 gap-3">
        {Object.values(UwSummarySection).map((section, index: number) => {
          const sectionFields = (inputGroups as any)[section] as string[]
          return (
            <div key={section}>
              <RenderInput
                Key={`section${index}`}
                input={{
                  inputType: 'section',
                  title: `${index + 1}. ${UwSummarySectionText[section]}`,
                }}
                onChange={() => {}}
              />
              <div className="grid grid-cols-1 gap-3">
                {sectionFields.map((key: string, subIndex: number) =>
                  renderField(section, key, subIndex + 1, uwSummaryInputs[key]?.title || key),
                )}
                {section === UwSummarySection.UnderwritingInformation &&
                  Object.keys(loanInputs).map((key, fieldIndex) =>
                    renderField(
                      section,
                      key,
                      fieldIndex + 1 + sectionFields.length,
                      loanInputs[key]?.title || key,
                    ),
                  )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex flex-row justify-center gap-4">
        <Button onClick={onSubmit} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>

      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
